import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import type { InquiryTimelineEvent } from "../../../api/supplierInquiriesApi"
import { inquiryScopeText } from "../../../utils/inquiryScope"
import { regulationKeyLabels } from "../../../utils/regulationLabel"

type Translate = (
  key: TranslationKeys,
  values?: Record<string, string | number>,
) => string

// Backend `detail_key` → catalog key. Keys carry their params verbatim
// ({to}, {sender}, {n}, {filename}); composition beyond a single key
// (Cc, regulations, trigger detail) happens below.
const DETAIL_KEY: Partial<Record<string, TranslationKeys>> = {
  reminded_manual: "MC_INQ_TLD_REMINDED_MANUAL",
  reminded_auto: "MC_INQ_TLD_REMINDED_AUTO",
  reply: "MC_INQ_TLD_REPLY",
  reply_auto: "MC_INQ_TLD_REPLY_AUTO",
  reply_quarantined: "MC_INQ_TLD_REPLY_QUARANTINED",
  reply_bounce: "MC_INQ_TLD_REPLY_BOUNCE",
  reply_received: "MC_INQ_TLD_REPLY_GENERIC",
  declaration_linked: "MC_INQ_TLD_DECLARATION_LINKED",
  declaration_held: "MC_INQ_TLD_DECLARATION_HELD",
  declaration_demoted: "MC_INQ_TLD_DECLARATION_DEMOTED",
  declaration_additional: "MC_INQ_TLD_DECLARATION_ADDITIONAL",
  attachment_rejected: "MC_INQ_TLD_ATTACHMENT_REJECTED",
  attachment_rejected_unsupported: "MC_INQ_TLD_ATTACHMENT_UNSUPPORTED",
  declaration_received_again: "MC_INQ_TLD_RECEIVED_AGAIN",
  supplier_message_answered: "MC_INQ_TLD_MESSAGE_ANSWERED",
  supplier_message_answer_failed: "MC_INQ_TLD_MESSAGE_ANSWERED",
  supplier_message_done: "MC_INQ_TLD_MESSAGE_DONE",
  escalated: "MC_INQ_TLD_ESCALATED",
  escalated_paused: "MC_INQ_TLD_ESCALATED_PAUSED",
  closed_resolved: "MC_INQ_TLD_CLOSED_RESOLVED",
  closed_with_reason: "MC_INQ_TLD_CLOSED_WITH_REASON",
  committed: "MC_INQ_TLD_COMMITTED",
  auto_reminders_off: "MC_INQ_TLD_AUTO_REMINDERS_OFF",
  auto_reminders_on: "MC_INQ_TLD_AUTO_REMINDERS_ON",
  auto_reminders_inherit: "MC_INQ_TLD_AUTO_REMINDERS_INHERIT",
  supplier_warning_acknowledged: "MC_INQ_TLD_SUPPLIER_WARNING_ACKNOWLEDGED",
  recipient_overlap_acknowledged: "MC_INQ_TLD_RECIPIENT_OVERLAP_ACKNOWLEDGED",
}

// Note events carry what the user wrote (or a file name) as their detail: there is
// nothing to translate, the param is shown as is. A row without it falls back to the
// backend note like any other. The backend's `file_name` arrives camelCased.
const NOTE_TEXT_PARAM: Partial<Record<string, string>> = {
  note_added: "body",
  note_edited: "body",
  note_removed: "body",
  note_attachment_added: "fileName",
  note_attachment_removed: "fileName",
}

const TRIGGER_KEY: Partial<Record<string, TranslationKeys>> = {
  manual: "MC_TRIGGER_MANUAL",
  customer_gap: "MC_TRIGGER_CUSTOMER_GAP",
  stale_data: "MC_TRIGGER_STALE_DATA",
  svhc_update: "MC_TRIGGER_SVHC_UPDATE",
  declaration_review: "MC_TRIGGER_DECLARATION_REVIEW",
}

/** "Waived: TSCA" for a waiver row's `regulations` (canonical keys comma-joined). */
function waivedText(regulations: string, translate: Translate): string {
  return translate("MC_INQ_TLD_REGULATIONS_WAIVED", {
    regulations: regulationKeyLabels(regulations.split(","), translate),
  })
}

/** The note a row cites, with the plain copy that leads to it (if any), or null for a
 *  row that cites none. The section sets the note apart instead of printing it as part
 *  of the subtitle; `timelineDetailText` still returns the whole line as plain text. */
export function timelineNoteQuote(
  event: Pick<InquiryTimelineEvent, "detailKey" | "detailParams">,
  translate: Translate,
): { lead: string | null; note: string } | null {
  const key = event.detailKey
  const p = event.detailParams ?? {}
  if (!key) return null
  if (NOTE_TEXT_PARAM[key] === "body" && p.body)
    return { lead: null, note: p.body }
  if (key === "closed_with_reason" && p.reason)
    return { lead: translate("MC_INQ_TLD_CLOSED_REASON_LABEL"), note: p.reason }
  if (key === "closed_resolved" && p.reason)
    return {
      lead: `${translate("MC_INQ_TLD_CLOSED_RESOLVED")} ·`,
      note: p.reason,
    }
  if (key === "regulations_waived" && p.regulations && p.reason)
    return { lead: `${waivedText(p.regulations, translate)} ·`, note: p.reason }
  return null
}

/** Localized subtitle for a timeline row. Falls back to the backend's English
 *  `subtitle` (the persisted audit note) when the row carries no detail key,
 *  e.g. bounce diagnostics or an event type this client does not know yet. */
export function timelineDetailText(
  event: Pick<InquiryTimelineEvent, "subtitle" | "detailKey" | "detailParams">,
  translate: Translate,
): string | null {
  const key = event.detailKey
  const p = event.detailParams ?? {}
  if (!key) return event.subtitle
  if (key === "drafted") {
    const triggerKey = TRIGGER_KEY[p.trigger]
    const trigger = triggerKey ? translate(triggerKey) : p.trigger
    const base = translate("MC_INQ_TLD_DRAFTED", { trigger })
    return p.detail ? `${base} · ${p.detail}` : base
  }
  if (key === "sent") {
    const parts: string[] = []
    if (p.to) parts.push(translate("MC_INQ_TLD_SENT_TO", { to: p.to }))
    if (p.cc) parts.push(translate("MC_INQ_TLD_SENT_CC", { cc: p.cc }))
    // `regulations`: what the documents asked, canonical keys comma-joined.
    if (p.regulations) {
      const scope = inquiryScopeText(
        { askedRegulations: p.regulations.split(",") },
        translate,
      )
      if (scope) parts.push(scope)
    }
    return parts.length > 0 ? parts.join(" · ") : event.subtitle
  }
  // `regulations`: what a partial answer left open, canonical keys comma-joined.
  // A waiver: what was waived, then the reason (`timelineNoteQuote` sets it apart).
  if (key === "regulations_waived") {
    if (!p.regulations) return event.subtitle
    const waived = waivedText(p.regulations, translate)
    return p.reason ? `${waived} · ${p.reason}` : waived
  }
  // `_removed`: a reply under review for them was deleted, not a declaration approved.
  if (key === "partially_answered" || key === "partially_answered_removed") {
    if (!p.regulations) return event.subtitle
    const list = regulationKeyLabels(p.regulations.split(","), translate)
    return translate(
      key === "partially_answered"
        ? "MC_INQ_TLD_PARTIALLY_ANSWERED"
        : "MC_INQ_TLD_PARTIALLY_ANSWERED_REMOVED",
      { regulations: list },
    )
  }
  if (key === "reminded_manual" || key === "reminded_auto") {
    if (p.to) {
      return translate(
        key === "reminded_auto"
          ? "MC_INQ_TLD_REMINDED_AUTO"
          : "MC_INQ_TLD_REMINDED_MANUAL",
        p,
      )
    }
    return translate(
      key === "reminded_auto"
        ? "MC_INQ_TLD_REMINDED_AUTO_NO_TO"
        : "MC_INQ_TLD_REMINDED_MANUAL_NO_TO",
      p,
    )
  }
  if (
    (key === "attachment_rejected" ||
      key === "attachment_rejected_unsupported") &&
    !p.filename
  ) {
    return translate(
      key === "attachment_rejected_unsupported"
        ? "MC_INQ_TLD_ATTACHMENT_UNSUPPORTED_NO_FILE"
        : "MC_INQ_TLD_ATTACHMENT_REJECTED_NO_FILE",
    )
  }
  const noteTextParam = NOTE_TEXT_PARAM[key]
  if (noteTextParam) return p[noteTextParam] || event.subtitle
  // A close that came with a declaration keeps its own sentence; the reason follows.
  if (key === "closed_resolved" && p.reason) {
    return `${translate("MC_INQ_TLD_CLOSED_RESOLVED")} · ${p.reason}`
  }
  if (key === "closed_with_reason" && !p.reason) return event.subtitle
  if (key === "supplier_warning_acknowledged" && !p.n) return event.subtitle
  if (key === "recipient_overlap_acknowledged" && (!p.n || !p.address))
    return event.subtitle
  if (key === "declaration_received_again" && !p.filename) return event.subtitle
  if (
    (key === "supplier_message_answered" ||
      key === "supplier_message_answer_failed") &&
    !p.to
  )
    return event.subtitle
  const catalogKey = DETAIL_KEY[key]
  return catalogKey ? translate(catalogKey, p) : event.subtitle
}
